<template>
  <div :class="['chips', { profile }]">
    <dropdown
      v-if="card.variation"
      :active="otherVariationsMenuOpen"
      :disabled="!showOtherVariations"
      :overflow-hidden="!profile"
      @close="otherVariationsMenuOpen = false"
    >
      <grade-variation-chip
        :as="showOtherVariations ? 'button' : 'span'"
        :dropdown="showOtherVariations"
        :variation="card.variation"
        :profile="profile"
        @click.native="otherVariationsMenuOpen = showOtherVariations && !otherVariationsMenuOpen"
      />

      <template #menu>
        <dropdown-menu
          :options="otherVariationOptions"
          :max-height="200"
          @select="switchCard"
        />
      </template>
    </dropdown>

    <template v-if="card.grader && card.grade && !card.cardId">
      <grade-variation-chip
        :grade="gradeDisplay(card.grader, card.grade)"
      />

      <grade-variation-chip
        v-if="card.autographGrade"
        :grade="autographGradeDisplay(card.autographGrade)"
        no-color
      />
    </template>

    <template v-else>
      <dropdown
        :active="otherGradesMenuOpen"
        :disabled="!showOtherGrades"
        :overflow-hidden="!profile"
        @close="otherGradesMenuOpen = false"
      >
        <grade-variation-chip
          :as="showOtherGrades ? 'button' : 'span'"
          :grade="card.condition"
          :dropdown="showOtherGrades"
          @click.native="otherGradesMenuOpen = showOtherGrades && !otherGradesMenuOpen"
        />

        <template #menu>
          <dropdown-menu
            :options="otherGradeOptions"
            :max-height="200"
            @select="switchCard"
          />
        </template>
      </dropdown>
    </template>

    <grade-variation-chip
      v-if="card.qualifierType"
      :grade="card.qualifierType"
      no-color
    />

    <icon
      class="key-card-icon"
      v-if="card.keyCard"
      name="stars"
      v-tooltip="{ content: `This card has been selected as the Key Card and representation of the general market trend for ${card.player}.`, delay: { show: 1000, hide: 100 }, placement: 'bottom' }"
    />

    <slot />
  </div>
</template>

<script>
import { sortOtherGrades, getGradeDisplay, getAutographGradeDisplay } from '../utils/format.util';

import Dropdown from './Dropdown';
import DropdownMenu from './DropdownMenu';
import GradeVariationChip from './GradeVariationChip';
import Icon from './Icon';

export default {
  components: {
    Dropdown,
    DropdownMenu,
    GradeVariationChip,
    Icon,
  },

  props: {
    card: Object,
    profile: Boolean,
    isPublic: Boolean,
  },

  data() {
    return {
      otherGradesMenuOpen: false,
      otherVariationsMenuOpen: false,
    };
  },

  computed: {
    showOtherGrades() {
      return this.profile && this.card.otherGrades && this.card.otherGrades.length > 0;
    },

    showOtherVariations() {
      return this.profile && this.card.otherVariations && this.card.otherVariations.length > 0;
    },

    otherGradeOptions() {
      if (!this.card.otherGrades || this.card.otherGrades.length === 0) {
        return [];
      }

      return sortOtherGrades(this.card.otherGrades).map((otherGrade) => ({
        value: otherGrade.id,
        label: otherGrade.condition,
      }));
    },

    otherVariationOptions() {
      if (!this.card.otherVariations || this.card.otherVariations.length === 0) {
        return [];
      }

      return this.card.otherVariations.map((otherVariation) => ({
        value: otherVariation.id,
        label: otherVariation.variation,
      }));
    },
  },

  methods: {
    switchCard(id) {
      this.otherGradesMenuOpen = false;
      this.otherVariationsMenuOpen = false;

      const query = { ...this.$route.query };
      delete query.profile;

      if (this.isPublic) {
        this.$router.push(`/ladder/card/${id}`);
      } else if (this.$route.params.id) {
        this.$router.push({ params: { id }, query });
      } else {
        this.$router.push({ query: { ...query, cardId: id } });
      }
    },

    gradeDisplay(grader, grade) {
      return getGradeDisplay(grader, grade);
    },

    autographGradeDisplay(grade) {
      return getAutographGradeDisplay(grade);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.chips {
  display: flex;
  align-items: center;

  &.profile {
    flex-wrap: wrap;

    > * {
      margin-bottom: $gp * .5;
      max-width: 100%;
    }
  }

  > * {
    margin-right: $gp * .5;
  }
}

.key-card-icon {
  cursor: pointer;
}
</style>
